import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Wallet, Users, Trophy, Banknote, AlertTriangle } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Card, IconBadge } from '../../components/ui';
import { adminApi, AdminOverview, AdminReconciliation, AdminLedgerReport } from '../../api';

const rand = (n: number) => `R${n.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Stat: React.FC<{ icon: React.ReactNode; label: string; value: string; sub?: string; tone?: 'brand' | 'gold' }> = ({ icon, label, value, sub, tone = 'brand' }) => (
  <Card>
    <div className="flex items-center justify-between mb-2">
      <IconBadge tone={tone}>{icon}</IconBadge>
      <span className="text-xs text-gray-500">{label}</span>
    </div>
    <p className="text-2xl font-extrabold text-gray-900">{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
  </Card>
);

const AdminDashboard: React.FC = () => {
  const [data, setData] = useState<AdminOverview | null>(null);
  const [recon, setRecon] = useState<AdminReconciliation | null>(null);
  const [ledger, setLedger] = useState<AdminLedgerReport | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.getOverview().then(setData).catch(() => setError('Could not load dashboard'));
    adminApi.getReconciliation().then(setRecon).catch(() => {});
    adminApi.getLedger().then(setLedger).catch(() => {});
  }, []);

  return (
    <AdminLayout>
      {error && <div className="bg-red-500/15 text-red-200 border border-red-500/30 p-3 rounded-xl text-sm mb-4">{error}</div>}
      {!data ? (
        <div className="text-green-200/80 py-10 text-center">Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Stat icon={<TrendingUp />}
              label="Revenue" value={rand(data.revenue_zar)} sub="FairPlay fees collected" />
            <Stat icon={<Wallet />}
              label="Held for users" value={rand(data.wallet_liability_zar)} sub="wallet balances" />
            <Stat icon={<Banknote />} tone="gold"
              label="Pending payouts" value={rand(data.pending_withdrawals_zar)} sub={`${data.pending_withdrawals_count} awaiting`} />
            <Stat icon={<Users />}
              label="Golfers" value={`${data.users_total}`} sub={`${data.users_verified} verified`} />
            <Stat icon={<Trophy />} tone="gold"
              label="Games" value={`${data.games_total}`} sub={`${data.games_in_progress} live · ${data.games_completed} done`} />
            <Stat icon={<AlertTriangle />}
              label="Disputes" value={`${data.games_disputed}`} sub="need attention" />
          </div>

          {recon && (
            <Card className={`mt-4 text-sm ${recon.balanced ? 'text-emerald-800' : 'text-red-700'}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold">{recon.balanced ? '✅ Money reconciled' : '⚠️ Discrepancy found'}</span>
                <span className={`font-extrabold ${recon.balanced ? 'text-emerald-700' : 'text-red-700'}`}>
                  {recon.discrepancy_zar === 0 ? 'R0.00' : rand(recon.discrepancy_zar)}
                </span>
              </div>
              <div className="mt-2 text-xs text-gray-500 space-y-0.5">
                <div className="flex justify-between"><span>Expected held (deposits − fees − payouts)</span><span>{rand(recon.expected_held_zar)}</span></div>
                <div className="flex justify-between"><span>Actual (wallets + open-bet stakes)</span><span>{rand(recon.actual_held_zar)}</span></div>
                <div className="flex justify-between"><span>· wallet balances</span><span>{rand(recon.wallet_balances_zar)}</span></div>
                <div className="flex justify-between"><span>· reserved in open bets</span><span>{rand(recon.reserved_in_open_bets_zar)}</span></div>
                <div className="flex justify-between"><span>· reserved for pending payouts</span><span>{rand(recon.reserved_in_pending_withdrawals_zar)}</span></div>
              </div>
              {!recon.balanced && <p className="mt-2 text-xs font-semibold text-red-700">Money is unaccounted for — investigate before processing payouts.</p>}
            </Card>
          )}

          {ledger && (
            <Card className={`mt-4 text-sm ${ledger.mismatched_count === 0 ? 'text-emerald-800' : 'text-red-700'}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold">
                  {ledger.mismatched_count === 0 ? '✅ Every wallet matches its ledger' : `⚠️ ${ledger.mismatched_count} wallet(s) out of balance`}
                </span>
                <span className="text-xs text-gray-500">{ledger.wallets_checked} checked</span>
              </div>
              {ledger.mismatched_count > 0 && (
                <div className="mt-2 space-y-1">
                  {ledger.mismatches.slice(0, 8).map((m) => (
                    <div key={m.user_id} className="flex justify-between text-xs border-t border-gray-100 pt-1">
                      <span className="truncate mr-2">{m.user_name} · {m.user_email}</span>
                      <span className="font-semibold whitespace-nowrap">
                        {rand(m.stored_balance_zar)} vs {rand(m.ledger_balance_zar)} ({m.difference_zar > 0 ? '+' : ''}{rand(m.difference_zar)})
                      </span>
                    </div>
                  ))}
                  <p className="mt-1 text-xs font-semibold text-red-700">
                    A wallet's balance doesn't match its transaction history — investigate this account.
                  </p>
                </div>
              )}
            </Card>
          )}

          <Card className="mt-4 text-sm text-gray-600">
            <div className="flex justify-between py-1"><span>Gross deposits</span><span className="font-semibold text-gray-900">{rand(data.deposits_zar)}</span></div>
            <div className="flex justify-between py-1"><span>Withdrawals paid</span><span className="font-semibold text-gray-900">{rand(data.withdrawals_paid_zar)}</span></div>
            <div className="flex justify-between py-1 border-t border-gray-100 mt-1 pt-2"><span>Revenue (fees)</span><span className="font-semibold text-emerald-700">{rand(data.revenue_zar)}</span></div>
          </Card>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <Link to="/admin/withdrawals" className="bg-emerald-600 text-white rounded-2xl p-4 font-semibold text-center hover:bg-emerald-700 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-950">
              Process payouts{data.pending_withdrawals_count > 0 ? ` (${data.pending_withdrawals_count})` : ''}
            </Link>
            <Link to="/admin/disputes" className="bg-white text-gray-800 border border-gray-200 rounded-2xl p-4 font-semibold text-center hover:bg-gray-50 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-950">
              Review disputes{data.games_disputed > 0 ? ` (${data.games_disputed})` : ''}
            </Link>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
